/**
 * @fileoverview Snapchat Client Bulk Intake Form
 * CSV upload form for bulk client creation.
 * CSV format: Column 1 = First Name, Column 2 = Last Name, Column 3 = Email
 * Client ID format: first initial + last initial + 8 random digits
 */

import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import './CallForm/CallForm.css';

/**
 * SnapchatIntakeBulk component for bulk CSV upload of client information.
 * Features:
 * - CSV file drag & drop or click to upload
 * - Partner selection for bulk assignment
 * - Preview of parsed data before submission
 * - Auto-generated client IDs with duplicate checking
 * - Progress tracking during submission
 * - Results summary with success/failure counts
 * 
 * @returns {React.ReactElement} The bulk intake form interface
 */
const SnapchatIntakeBulk = () => {
  const [csvData, setCsvData] = useState([]);
  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const fileInputRef = useRef(null);

  /**
   * Fetches partners list on component mount.
   */
  useEffect(() => {
    fetchPartners();
  }, []);

  /**
   * Fetches list of partners from API.
   */
  const fetchPartners = async () => {
    try {
      setLoadingPartners(true);
      const response = await axios.get(API_ENDPOINTS.PARTNERS.LIST);
      const partnersData = Array.isArray(response.data) ? response.data : [];
      setPartners(partnersData);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  /**
   * Parses CSV text into array of objects.
   * @param {string} text - Raw CSV text
   * @returns {Array} Parsed data array
   */
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const data = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Handle both comma and tab delimiters
      const delimiter = line.includes('\t') ? '\t' : ',';
      const values = line.split(delimiter).map(v => v.trim().replace(/^["']|["']$/g, ''));
      
      // Skip header row if detected
      if (i === 0) {
        const firstVal = values[0]?.toLowerCase();
        if (firstVal === 'first name' || firstVal === 'firstname' || firstVal === 'first_name' || firstVal === 'first') {
          continue;
        }
      }
      
      if (values.length >= 3 && values[0] && values[1] && values[2]) {
        data.push({
          First_Legal_Name: values[0],
          Last_Legal_Name: values[1],
          Email_Address: values[2],
          client_id: '', // Will be generated
          status: 'pending'
        });
      }
    }
    
    return data;
  };

  /**
   * Handles file selection.
   * @param {File} file - The selected file
   */
  const handleFile = (file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setErrors({ file: 'Please upload a CSV or TXT file' });
      return;
    }
    
    setFileName(file.name);
    setErrors({});
    setResults(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);
      
      if (parsed.length === 0) {
        setErrors({ file: 'No valid data found in file. Expected: First Name, Last Name, Email Address' });
        return;
      }
      
      setCsvData(parsed);
    };
    reader.readAsText(file);
  };

  /**
   * Handles file input change.
   */
  const handleFileChange = (e) => {
    handleFile(e.target.files[0]);
  };

  /**
   * Handles drag over event.
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handles drag leave event.
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handles file drop.
   */
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  /**
   * Validates email format.
   * @param {string} email - Email to validate
   * @returns {boolean} Whether email is valid
   */
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  /**
   * Submits all clients from CSV.
   */
  const handleSubmit = async () => {
    if (csvData.length === 0) {
      setErrors({ submit: 'No data to submit' });
      return;
    }
    
    setLoading(true);
    setProgress({ current: 0, total: csvData.length });
    
    const results = {
      success: [],
      failed: []
    };
    
    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      setProgress({ current: i + 1, total: csvData.length });
      
      // Validate email
      if (!isValidEmail(row.Email_Address)) {
        results.failed.push({
          ...row,
          error: 'Invalid email address'
        });
        continue;
      }
      
      try {
        // Submit to backend - client ID is generated on server
        const response = await axios.post(API_ENDPOINTS.CLIENTS.INTAKE, {
          First_Legal_Name: row.First_Legal_Name,
          Last_Legal_Name: row.Last_Legal_Name,
          Email_Address: row.Email_Address,
          partner_id: selectedPartner?._id || null
        });
        
        if (response.data.status === 'success') {
          results.success.push({
            ...row,
            client_id: response.data.client_id,
            partner: response.data.partner || null
          });
        } else {
          results.failed.push({
            ...row,
            error: 'Submission failed'
          });
        }
      } catch (error) {
        results.failed.push({
          ...row,
          error: error.response?.data?.detail || error.message || 'Unknown error'
        });
      }
    }
    
    setResults(results);
    setLoading(false);
  };

  /**
   * Resets the form.
   */
  const handleReset = () => {
    setCsvData([]);
    setFileName('');
    setErrors({});
    setResults(null);
    setProgress({ current: 0, total: 0 });
    setSelectedPartner(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Removes a row from the preview.
   */
  const handleRemoveRow = (index) => {
    setCsvData(prev => prev.filter((_, i) => i !== index));
  };

  // Results view
  if (results) {
    return (
      <div className="call-form-page call-form">
        <div className="snapped-call-form__container">
          <h1 className="snapped-call-form__title">Bulk Import Results</h1>
          
          <div className="snapped-call-form__section">
            <div style={{ 
              display: 'flex', 
              gap: '40px', 
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '48px', 
                  fontWeight: '700', 
                  color: '#10b981',
                  background: 'transparent'
                }}>
                  {results.success.length}
                </div>
                <div style={{ color: '#666', background: 'transparent' }}>Successful</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '48px', 
                  fontWeight: '700', 
                  color: results.failed.length > 0 ? '#ef4444' : '#999',
                  background: 'transparent'
                }}>
                  {results.failed.length}
                </div>
                <div style={{ color: '#666', background: 'transparent' }}>Failed</div>
              </div>
            </div>
          </div>
          
          {results.success.length > 0 && (
            <div className="snapped-call-form__section">
              <h3 className="snapped-call-form__section-heading" style={{ color: '#10b981' }}>
                ✓ Successfully Created
              </h3>
              <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px', textAlign: 'left', background: '#f5f5f5' }}>Name</th>
                      <th style={{ padding: '8px', textAlign: 'left', background: '#f5f5f5' }}>Email</th>
                      <th style={{ padding: '8px', textAlign: 'left', background: '#f5f5f5' }}>Client ID</th>
                      <th style={{ padding: '8px', textAlign: 'left', background: '#f5f5f5' }}>Partner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.success.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px', background: 'transparent' }}>
                          {row.First_Legal_Name} {row.Last_Legal_Name}
                        </td>
                        <td style={{ padding: '8px', background: 'transparent' }}>{row.Email_Address}</td>
                        <td style={{ padding: '8px', background: 'transparent', color: '#4962BF', fontWeight: '600' }}>
                          {row.client_id}
                        </td>
                        <td style={{ padding: '8px', background: 'transparent', color: '#666' }}>
                          {row.partner || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {results.failed.length > 0 && (
            <div className="snapped-call-form__section">
              <h3 className="snapped-call-form__section-heading" style={{ color: '#ef4444' }}>
                ✗ Failed
              </h3>
              <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px', textAlign: 'left', background: '#f5f5f5' }}>Name</th>
                      <th style={{ padding: '8px', textAlign: 'left', background: '#f5f5f5' }}>Email</th>
                      <th style={{ padding: '8px', textAlign: 'left', background: '#f5f5f5' }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.failed.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px', background: 'transparent' }}>
                          {row.First_Legal_Name} {row.Last_Legal_Name}
                        </td>
                        <td style={{ padding: '8px', background: 'transparent' }}>{row.Email_Address}</td>
                        <td style={{ padding: '8px', background: 'transparent', color: '#ef4444' }}>
                          {row.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          <button
            type="button"
            className="snapped-call-form__submit-button"
            onClick={handleReset}
          >
            Import More
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="call-form-page call-form">
      <div className="snapped-call-form__container">
        <h1 className="snapped-call-form__title">Snapchat Client Intake Form</h1>
        <p style={{ 
          textAlign: 'center', 
          color: '#666', 
          marginBottom: '32px',
          background: 'transparent'
        }}>
          Bulk CSV Upload
        </p>
        
        <div className="snapped-call-form__section">
          <h3 className="snapped-call-form__section-heading">Assign to Partner</h3>
          <p style={{ 
            fontSize: '0.9rem', 
            color: '#666', 
            marginBottom: '16px',
            background: 'transparent'
          }}>
            Select a partner to assign all imported clients to (optional)
          </p>
          
          <select
            className="snapped-call-form__input"
            value={selectedPartner?._id || ''}
            onChange={(e) => {
              const partner = partners.find(p => p._id === e.target.value);
              setSelectedPartner(partner || null);
            }}
            disabled={loadingPartners}
            style={{ marginBottom: '0' }}
          >
            <option value="">-- No Partner (optional) --</option>
            {partners.map((partner) => (
              <option key={partner._id} value={partner._id}>
                {partner.name}
              </option>
            ))}
          </select>
          
          {selectedPartner && (
            <p style={{ 
              fontSize: '0.85rem', 
              color: '#4962BF', 
              marginTop: '8px',
              background: 'transparent'
            }}>
              ✓ All clients will be assigned to: <strong>{selectedPartner.name}</strong>
            </p>
          )}
        </div>
        
        <div className="snapped-call-form__section">
          <h3 className="snapped-call-form__section-heading">Upload CSV File</h3>
          <p style={{ 
            fontSize: '0.9rem', 
            color: '#666', 
            marginBottom: '16px',
            background: 'transparent'
          }}>
            CSV format: Column 1 = First Name, Column 2 = Last Name, Column 3 = Email Address
          </p>
          
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${isDragging ? '#4962BF' : '#ccc'}`,
              borderRadius: '12px',
              padding: '48px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragging ? 'rgba(73, 98, 191, 0.05)' : '#fafafa',
              transition: 'all 0.2s ease'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              background: 'transparent'
            }}>
              📄
            </div>
            <div style={{ 
              fontSize: '1.1rem', 
              color: '#333',
              marginBottom: '8px',
              background: 'transparent'
            }}>
              {fileName || 'Drop CSV file here or click to upload'}
            </div>
            <div style={{ 
              fontSize: '0.85rem', 
              color: '#999',
              background: 'transparent'
            }}>
              Supports .csv and .txt files
            </div>
          </div>
          
          {errors.file && (
            <span className="snapped-call-form__error-message" style={{ marginTop: '12px', display: 'block' }}>
              {errors.file}
            </span>
          )}
        </div>
        
        {csvData.length > 0 && (
          <>
            <div className="snapped-call-form__section">
              <h3 className="snapped-call-form__section-heading">
                Preview ({csvData.length} records)
              </h3>
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '10px', textAlign: 'left', background: '#f5f5f5' }}>First Name</th>
                      <th style={{ padding: '10px', textAlign: 'left', background: '#f5f5f5' }}>Last Name</th>
                      <th style={{ padding: '10px', textAlign: 'left', background: '#f5f5f5' }}>Email</th>
                      <th style={{ padding: '10px', textAlign: 'center', background: '#f5f5f5', width: '60px' }}>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvData.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px', background: 'transparent' }}>{row.First_Legal_Name}</td>
                        <td style={{ padding: '10px', background: 'transparent' }}>{row.Last_Legal_Name}</td>
                        <td style={{ 
                          padding: '10px', 
                          background: 'transparent',
                          color: isValidEmail(row.Email_Address) ? 'inherit' : '#ef4444'
                        }}>
                          {row.Email_Address}
                          {!isValidEmail(row.Email_Address) && (
                            <span style={{ marginLeft: '8px', fontSize: '0.8rem' }}>⚠️ Invalid</span>
                          )}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', background: 'transparent' }}>
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(i)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              color: '#999',
                              padding: '4px 8px'
                            }}
                            title="Remove row"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {loading && (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px',
                background: 'transparent'
              }}>
                <div style={{ 
                  marginBottom: '12px',
                  fontSize: '1rem',
                  color: '#4962BF',
                  background: 'transparent'
                }}>
                  Processing {progress.current} of {progress.total}...
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${(progress.current / progress.total) * 100}%`,
                    height: '100%',
                    backgroundColor: '#4962BF',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}
            
            {errors.submit && (
              <div className="snapped-call-form__error-message" style={{ marginBottom: '20px' }}>
                {errors.submit}
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={handleReset}
                style={{
                  flex: '1',
                  padding: '16px 32px',
                  border: '1px solid #ccc',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  color: '#666',
                  transition: 'all 0.2s ease'
                }}
                disabled={loading}
              >
                Clear
              </button>
              <button
                type="button"
                className="snapped-call-form__submit-button"
                onClick={handleSubmit}
                disabled={loading || csvData.length === 0}
                style={{ flex: '2', marginTop: '0' }}
              >
                {loading ? `Processing (${progress.current}/${progress.total})...` : `Submit ${csvData.length} Records`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SnapchatIntakeBulk;

